import { Link } from "react-router-dom";
import { useEffect } from "react";
import { Phone, MessageCircle, MapPin, Globe, Mail, Instagram, Facebook, Youtube, Star } from "lucide-react";
import { SITE, waLink } from "../data/site";
import { Logo } from "./Header";
import { track } from "../lib/track";

const Column = ({ title, children }) => (
  <div>
    <h3 className="text-xs font-bold uppercase tracking-[0.25em] text-white/50">{title}</h3>
    <ul className="mt-5 space-y-3 text-sm">{children}</ul>
  </div>
);

const VisitTracker = () => {
  useEffect(() => {
    if (sessionStorage.getItem("dwc-visited")) return;
    sessionStorage.setItem("dwc-visited", "1");
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/visits`, { method: "POST" }).catch(() => {});
  }, []);
  return null;
};

const CONTACT_LINES = [
  { label: "Preferred to Chat", prefix: "", display: "+91 77780 80808", href: waLink(), whatsapp: true, testId: "footer-chat-whatsapp" },
  { label: "Existing Customer", prefix: "Preferred to talk:", display: "+91 77790 90909", href: "tel:+917779090909", testId: "footer-phone-existing" },
  { label: "Franchise Inquiry", prefix: "Preferred to talk:", display: "+91 77788 33333", href: "tel:+917778833333", testId: "footer-phone-franchise" },
];

const FooterLink = ({ to, children, testId }) => (
  <li>
    <Link to={to} data-testid={testId} className="text-white/70 transition-colors duration-200 hover:text-white">
      {children}
    </Link>
  </li>
);

const Footer = () => (
  <footer className="relative bg-dwc-charcoal pb-28 pt-20 text-white md:pb-20" data-testid="site-footer">
    <VisitTracker />
    <div className="mx-auto max-w-7xl px-5 lg:px-8">
      <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-5">
        <div className="lg:col-span-2">
          <Logo dark />
          <p className="mt-6 max-w-sm text-sm leading-relaxed text-white/60">
            Practical driving training combined with driving psychology — helping nervous, hesitant and new drivers in Mumbai, Surat & Ahmedabad become confident on the road.
          </p>
          <p className="mt-4 flex max-w-sm items-start gap-2 text-sm leading-relaxed text-white/60" data-testid="footer-address">
            <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-dwc-green" aria-hidden="true" />
            Address: 5th Floor, Wing 'B', Supreme Business Park, B-501, Supreme City, Powai, Mumbai, Maharashtra 400076
          </p>
          <p className="mt-6 inline-flex items-center gap-2 rounded-full border border-dwc-green/40 bg-dwc-green/15 px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.22em] text-emerald-200">
            <span className="h-1.5 w-1.5 rounded-full bg-dwc-crimson" aria-hidden="true" />
            {SITE.tagline}
          </p>
        </div>
        <Column title="Explore">
          <FooterLink to="/why-dwc" testId="footer-why-dwc">Why DWC</FooterLink>
          <FooterLink to="/driving-psychology" testId="footer-driving-psychology">Driving Psychology</FooterLink>
          <FooterLink to="/programs" testId="footer-programs">Programs</FooterLink>
          <FooterLink to="/success-stories" testId="footer-success-stories">Success Stories</FooterLink>
          <FooterLink to="/guarantee" testId="footer-guarantee">Guarantee</FooterLink>
        </Column>
        <Column title="Services">
          <FooterLink to="/programs/learning-dna" testId="footer-beginner">Learning DNA</FooterLink>
          <FooterLink to="/programs/reaction-dna" testId="footer-confidence">Reaction DNA</FooterLink>
          <FooterLink to="/programs/confidence-dna" testId="footer-independence">Confidence DNA</FooterLink>
          <FooterLink to="/women-driving-training" testId="footer-women">Women Driving Training</FooterLink>
          <FooterLink to="/senior-citizen-driving" testId="footer-senior">Senior Citizen Training</FooterLink>
          <FooterLink to="/advanced-driving-training" testId="footer-advanced">Advanced Training</FooterLink>
          <FooterLink to="/rto-consulting" testId="footer-rto">RTO Consulting</FooterLink>
          <FooterLink to="/learning-licence" testId="footer-learning-licence">Learning Licence</FooterLink>
        </Column>
        <Column title="Useful Links">
          <FooterLink to="/blog" testId="footer-blog">Blog</FooterLink>
          <FooterLink to="/franchise" testId="footer-franchise">Franchise</FooterLink>
          <FooterLink to="/contact" testId="footer-privacy">Privacy Policy</FooterLink>
          <FooterLink to="/contact" testId="footer-terms">Terms & Conditions</FooterLink>
          <FooterLink to="/guarantee" testId="footer-refund">Refund Policy</FooterLink>
        </Column>
      </div>
      <div className="mt-14 border-t border-white/10 pt-8">
        <ul className="space-y-3" data-testid="footer-contact-lines">
          {CONTACT_LINES.map((c, i) => (
            <li key={c.testId} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
              <span className="font-bold text-white/85">{i + 1}. {c.label}</span>
              <a
                href={c.href}
                {...(c.whatsapp ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                data-testid={c.testId}
                onClick={() => track(c.whatsapp ? "whatsapp_click" : "phone_click", { source: `footer_${c.testId}` })}
                className="inline-flex items-center gap-1.5 font-bold text-dwc-green transition-colors hover:text-white"
              >
                {c.whatsapp ? <MessageCircle className="h-4 w-4" aria-hidden="true" /> : <Phone className="h-4 w-4" aria-hidden="true" />}
                {c.prefix} {c.display}
              </a>
            </li>
          ))}
        </ul>
      </div>
      <div className="mt-8 flex flex-col gap-6 border-t border-white/10 pt-8 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-white/70">
          <span className="inline-flex items-center gap-2" data-testid="footer-cities"><MapPin className="h-4 w-4 text-dwc-green" aria-hidden="true" />Mumbai, Surat and Ahmedabad</span>
          <a href={waLink()} target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp" onClick={() => track("whatsapp_click", { source: "footer" })} className="inline-flex items-center gap-2 transition-colors hover:text-white">
            <MessageCircle className="h-4 w-4 text-dwc-green" aria-hidden="true" />WhatsApp
          </a>
          {SITE.email && (
            <a href={`mailto:${SITE.email}`} data-testid="footer-email" className="inline-flex items-center gap-2 transition-colors hover:text-white">
              <Mail className="h-4 w-4 text-dwc-green" aria-hidden="true" />{SITE.email}
            </a>
          )}
          <a href={SITE.url} target="_blank" rel="noopener noreferrer" data-testid="footer-website" className="inline-flex items-center gap-2 transition-colors hover:text-white">
            <Globe className="h-4 w-4 text-dwc-green" aria-hidden="true" />drivewithconfidence.in
          </a>
          {SITE.instagram && (
            <a href={SITE.instagram} target="_blank" rel="noopener noreferrer" data-testid="footer-instagram" aria-label="Instagram" className="transition-colors hover:text-white">
              <Instagram className="h-4 w-4 text-dwc-green" aria-hidden="true" />
            </a>
          )}
          {SITE.facebook && (
            <a href={SITE.facebook} target="_blank" rel="noopener noreferrer" data-testid="footer-facebook" aria-label="Facebook" className="transition-colors hover:text-white">
              <Facebook className="h-4 w-4 text-dwc-green" aria-hidden="true" />
            </a>
          )}
          {SITE.youtube && (
            <a href={SITE.youtube} target="_blank" rel="noopener noreferrer" data-testid="footer-youtube" aria-label="YouTube" className="transition-colors hover:text-white">
              <Youtube className="h-4 w-4 text-dwc-green" aria-hidden="true" />
            </a>
          )}
          {SITE.googleReview && (
            <a href={SITE.googleReview} target="_blank" rel="noopener noreferrer" data-testid="footer-google-reviews" className="inline-flex items-center gap-2 transition-colors hover:text-white">
              <Star className="h-4 w-4 text-dwc-green" aria-hidden="true" />{SITE.googleRating}★ · {SITE.googleReviewCount} Google reviews
            </a>
          )}
        </div>
        <p className="text-xs text-white/40">© {new Date().getFullYear()} Drive With Confidence. All rights reserved.</p>
      </div>
    </div>
  </footer>
);

export default Footer;
